import { SAFE_VIDEO_CODECS, sniffVideoCodec, type ByteReader, type VideoCodec, type VideoCodecInfo } from "@69ly/shared";
import { useEffect, useState } from "react";

/** Reads a picked file in slices: the video never loads whole. */
const fileReader =
  (file: Blob): ByteReader =>
  async (offset, length) =>
    new Uint8Array(await file.slice(offset, offset + length).arrayBuffer());

/** The codec of a video picked for upload, or null when it cannot tell (WebM, a damaged file). */
export function codecOfFile(file: Blob): Promise<VideoCodecInfo | null> {
  return sniffVideoCodec(fileReader(file), file.size).catch(() => null);
}

/**
 * The codec of a stored video, in a few Range requests (a few KB). Null when the
 * server does not answer in parts: then it would download the whole video.
 */
export async function codecOfUrl(url: string): Promise<VideoCodecInfo | null> {
  try {
    const first = await fetch(url, { headers: { range: "bytes=0-15" }, credentials: "same-origin" });
    const size = Number(/\/(\d+)$/.exec(first.headers.get("content-range") ?? "")?.[1]);
    if (first.status !== 206 || !Number.isFinite(size)) {
      await first.body?.cancel();
      return null;
    }
    const head = new Uint8Array(await first.arrayBuffer());
    const read: ByteReader = async (offset, length) => {
      if (offset === 0 && length <= head.length) return head.subarray(0, length);
      const res = await fetch(url, { headers: { range: `bytes=${offset}-${offset + length - 1}` }, credentials: "same-origin" });
      if (res.status !== 206) {
        await res.body?.cancel();
        return new Uint8Array(0);
      }
      return new Uint8Array(await res.arrayBuffer());
    };
    return await sniffVideoCodec(read, size);
  } catch {
    return null;
  }
}

const NAMES: Record<VideoCodec, string> = {
  h264: "H.264",
  hevc: "H.265 (HEVC)",
  av1: "AV1",
  vp9: "VP9",
  vp8: "VP8",
  prores: "ProRes",
  other: "",
};

/** "H.265 (HEVC)"; an unknown codec by its four letters. */
export const codecName = (c: VideoCodecInfo) => NAMES[c.codec] || c.tag.trim();

/** A codec some browsers show with sound only, or not at all. */
export const riskyCodec = (c: VideoCodecInfo | null) => !!c && !SAFE_VIDEO_CODECS.includes(c.codec);

/** The codec of a picked file, once read (undefined while reading). */
export function useFileCodec(file: File | null): VideoCodecInfo | null | undefined {
  const [codec, setCodec] = useState<{ file: File | null; info: VideoCodecInfo | null } | null>(null);
  useEffect(() => {
    if (!file) return;
    let live = true;
    void codecOfFile(file).then((info) => live && setCodec({ file, info }));
    return () => {
      live = false;
    };
  }, [file]);
  if (!file) return null;
  return codec?.file === file ? codec.info : undefined;
}
